import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { useSetClaimFileDocumentType } from "../api/use-claim-mutations";
import type { ClaimDocumentType } from "../lib/document-types";
import { ClaimDocumentTypeCombobox } from "./claim-document-fields";

export interface DocumentToRetype {
  fileId: string;
  name: string;
  /** Null: a file of the explorer not classified yet. */
  documentType: ClaimDocumentType | null;
}

interface ChangeDocumentTypeDialogProps {
  claimId: string;
  /** Null when closed. */
  document: DocumentToRetype | null;
  onClose: () => void;
}

/**
 * « Changer le type » of a claim's document, rather than deleting and adding it again (TECH-1391). Shared by the
 * claim's documents and the file explorer. The API refuses a type the same file already has in the claim: the
 * toast says so and the dialog stays open.
 */
export function ChangeDocumentTypeDialog({ claimId, document, onClose }: ChangeDocumentTypeDialogProps) {
  const setDocumentType = useSetClaimFileDocumentType(claimId);
  const [documentType, setSelectedType] = useState<ClaimDocumentType>("OTHER");

  useEffect(() => {
    if (document) {
      setSelectedType(document.documentType ?? "OTHER");
    }
  }, [document]);

  function submit() {
    if (!document) {
      return;
    }
    setDocumentType.mutate(
      { fileId: document.fileId, claimDocumentType: documentType },
      { onSuccess: onClose },
    );
  }

  const isUnchanged = documentType === document?.documentType;

  return (
    <Dialog open={document !== null} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {document?.documentType === null ? "Classer le document" : "Changer le type du document"}
          </DialogTitle>
          <DialogDescription className="truncate" title={document?.name}>
            {document?.name}
          </DialogDescription>
        </DialogHeader>
        <DialogPanel>
          <div className="py-2 text-left">
            <Label className="mb-1 block text-sm font-medium text-gray-700">Type de document</Label>
            <ClaimDocumentTypeCombobox value={documentType} onChange={setSelectedType} />
          </div>
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Annuler</DialogClose>
          <Button onClick={submit} disabled={isUnchanged} loading={setDocumentType.isPending}>
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
